import { useState } from "react"
import { motion } from "framer-motion"
import { IMAGES } from "@/assets/images"
import { ASSISTANTS_LIST } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion"
import Layout from "@/components/Layout"
import AssistantCard from "@/components/AssistantCard"
import SectionHeader from "@/components/SectionHeader"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  BookOpen,
  Target,
  FileText,
  Calculator,
  Sparkles,
  Users,
  Calendar,
  Heart,
  MessageSquare,
  ClipboardList,
  ClipboardCheck,
  ListChecks,
  CheckCircle,
  FileCheck,
  MessageCircle,
  Wallet,
  Briefcase,
  Megaphone,
  Scale,
  Settings,
  Flag,
  Globe,
  Quote,
} from "lucide-react"

const iconMap = {
  BookOpen,
  Target,
  FileText,
  Calculator,
  Sparkles,
  Users,
  Calendar,
  Heart,
  MessageSquare,
  ClipboardList,
  ClipboardCheck,
  ListChecks,
  CheckCircle,
  FileCheck,
  MessageCircle,
  Wallet,
  Briefcase,
  Megaphone,
  Scale,
  Settings,
}

const curriculumData = [
  {
    id: "caps",
    name: "CAPS",
    country: "South Africa",
    icon: Flag,
  },
  {
    id: "ieb",
    name: "IEB",
    country: "South Africa",
    icon: Flag,
  },
  {
    id: "cambridge",
    name: "Cambridge",
    country: "International",
    icon: Globe,
  },
]

const schoolBenefits = [
  {
    category: "Learners",
    benefits: [
      {
        icon: "BookOpen",
        title: "Homework Help",
        description: "Get instant support with assignments across all subjects",
      },
      {
        icon: "Target",
        title: "Exam Preparation",
        description: "Practice tests, study plans and revision guidance",
      },
      {
        icon: "FileText",
        title: "Study Notes",
        description: "Curriculum-aligned summaries and explanations",
      },
      {
        icon: "Calculator",
        title: "Step-by-Step Solutions",
        description: "Learn problem-solving methods, not just answers",
      },
      {
        icon: "Sparkles",
        title: "Personalized Learning",
        description: "Adaptive support based on individual needs",
      },
      {
        icon: "MessageSquare",
        title: "24/7 Availability",
        description: "Get help whenever you need it, day or night",
      },
    ],
  },
  {
    category: "Parents",
    benefits: [
      {
        icon: "Users",
        title: "Support Your Child",
        description: "Help with homework even if you don't know the subject",
      },
      {
        icon: "Calendar",
        title: "Track Progress",
        description: "Stay informed about your child's learning journey",
      },
      {
        icon: "Heart",
        title: "Reduce Stress",
        description: "No more homework battles or expensive tutors",
      },
      {
        icon: "MessageCircle",
        title: "Easy Communication",
        description: "Get updates and support via WhatsApp",
      },
      {
        icon: "Wallet",
        title: "Affordable Access",
        description: "Quality education support at a fraction of tutor costs",
      },
      {
        icon: "CheckCircle",
        title: "Peace of Mind",
        description: "Know your child has reliable academic support",
      },
    ],
  },
  {
    category: "Teachers",
    benefits: [
      {
        icon: "ClipboardList",
        title: "Lesson Planning",
        description: "Ready-to-use lesson plans aligned to curriculum",
      },
      {
        icon: "FileCheck",
        title: "Worksheets & Assessments",
        description: "Generate quality materials in minutes",
      },
      {
        icon: "ListChecks",
        title: "Rubrics & Marking Guides",
        description: "Consistent assessment tools for all subjects",
      },
      {
        icon: "ClipboardCheck",
        title: "Report Comments",
        description: "Professional, personalized feedback for learners",
      },
      {
        icon: "Calendar",
        title: "Term Planning",
        description: "Organize your year with curriculum-aligned schedules",
      },
      {
        icon: "Sparkles",
        title: "Save Time",
        description: "Focus on teaching, not admin and preparation",
      },
    ],
  },
  {
    category: "Schools",
    benefits: [
      {
        icon: "Briefcase",
        title: "Competitive Advantage",
        description: "Offer modern support that attracts and retains families",
      },
      {
        icon: "Megaphone",
        title: "Marketing Tool",
        description: "Stand out with innovative education technology",
      },
      {
        icon: "Scale",
        title: "Scalable Support",
        description: "Extend help beyond classroom hours and resources",
      },
      {
        icon: "Settings",
        title: "Easy Implementation",
        description: "No complex IT setup or training required",
      },
      {
        icon: "Users",
        title: "Community Building",
        description: "Connect learners, parents and teachers on one platform",
      },
      {
        icon: "Target",
        title: "Improved Outcomes",
        description: "Better academic results through consistent support",
      },
    ],
  },
]

const staffAssistants = ASSISTANTS_LIST.filter((a) =>
  ["money-coach", "mental-support", "healthy-food", "legal-guide", "home-fix-build", "business-assist"].includes(a.id)
)

export default function Schools() {
  const [activeTab, setActiveTab] = useState("Learners")

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.EDUCATION_8}
              alt="Education background"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                Better support for learners, parents, teachers and schools
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Practical education support through familiar messaging platforms. From homework help to school
                management, Sammo brings smarter support to every part of school life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8">
                  See Sammo for Schools
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8">
                  Book a Demo
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Curriculum Support"
              headline="Built for your curriculum"
              subheadline="Aligned with South African curricula and adaptable to other countries"
              align="center"
              className="mb-16"
            />

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            >
              {curriculumData.map((curriculum) => {
                const Icon = curriculum.icon
                return (
                  <motion.div key={curriculum.id} variants={staggerItem}>
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                          <Icon className="w-8 h-8 text-accent" />
                        </div>
                        <h3 className="text-2xl font-bold mb-2">{curriculum.name}</h3>
                        <p className="text-muted-foreground">{curriculum.country}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Badge variant="secondary" className="text-base px-6 py-2">
                Adaptable to other countries and curricula
              </Badge>
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Support for Everyone"
              headline="Practical help for every role"
              subheadline="From learners to school leadership, Sammo provides targeted support"
              align="center"
              className="mb-16"
            />

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-12">
                  {schoolBenefits.map((benefit) => (
                    <TabsTrigger key={benefit.category} value={benefit.category} className="text-base">
                      {benefit.category}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {schoolBenefits.map((benefit) => (
                  <TabsContent key={benefit.category} value={benefit.category}>
                    <motion.div
                      variants={staggerContainer}
                      initial="hidden"
                      animate="visible"
                      className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                      {benefit.benefits.map((item) => {
                        const Icon = iconMap[item.icon as keyof typeof iconMap]
                        if (!Icon) return null
                        return (
                          <motion.div key={item.title} variants={staggerItem}>
                            <Card className="h-full hover:shadow-lg transition-shadow">
                              <CardContent className="p-6">
                                <div className="w-12 h-12 mb-4 rounded-lg bg-accent/10 flex items-center justify-center">
                                  <Icon className="w-6 h-6 text-accent" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                                <p className="text-muted-foreground text-sm">{item.description}</p>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )
                      })}
                    </motion.div>
                  </TabsContent>
                ))}
              </Tabs>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Bonus for Staff"
              headline="Support your teachers beyond the classroom"
              subheadline="Give your staff access to practical support for their personal lives"
              align="center"
              className="mb-16"
            />

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
            >
              {staffAssistants.map((assistant) => (
                <motion.div key={assistant.id} variants={staggerItem}>
                  <AssistantCard assistant={assistant} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <Card className="bg-primary text-primary-foreground">
                <CardContent className="p-12">
                  <Quote className="w-12 h-12 mb-6 opacity-50" />
                  <blockquote className="text-2xl md:text-3xl font-medium mb-6">
                    "Sammo has transformed how we support our learners and teachers. From homework help to lesson
                    planning, it's become an essential tool for our entire school community."
                  </blockquote>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                      <Users className="w-8 h-8" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Principal Sarah Nkosi</p>
                      <p className="opacity-80">Johannesburg Secondary School</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Bring smarter support to your school</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join schools across South Africa using Sammo to support learners, parents, teachers, and management.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8">
                  Get Started
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8">
                  Schedule a Call
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  )
}